/**
 * Российский номер в поле заказа.
 *
 * Курьер несёт заказ к лавочке и звонит, если не может найти гостя. Номер,
 * по которому нельзя позвонить, обесценивает весь заказ: еда остынет, а
 * доставщик будет ходить по парку и заглядывать в лица.
 *
 * Поэтому поле не принимает произвольные цифры: код страны подставляется
 * сам, лишние цифры отбрасываются. Ввести иностранный номер тут нельзя —
 * точка одна и работает в парке, а не в аэропорту.
 */

/** Сколько цифр в российском номере вместе с кодом страны. */
export const RU_PHONE_DIGITS = 11;

/**
 * Цифры номера в каноническом виде: 7 и десять цифр.
 *
 * Восьмёрку в начале меняем на семёрку, а номер, начатый с девятки,
 * дополняем кодом страны: гость набирает так, как привык, а не так, как
 * удобно системе.
 *
 * А вот длинную строку, не похожую на российский номер, не трогаем.
 * Подставить семёрку к вставленному «380501234567» значит превратить его
 * в правдоподобный чужой номер: гость опечатки не заметит, а доставщик
 * позвонит незнакомому человеку. Пусть лучше поле честно скажет, что номер
 * не подходит.
 */
export function ruPhoneDigits(raw: string): string {
  let digits = (raw ?? "").replace(/\D/g, "");
  if (!digits) return "";

  if (digits.startsWith("8")) digits = `7${digits.slice(1)}`;
  else if (!digits.startsWith("7") && digits.length <= RU_PHONE_DIGITS - 1) digits = `7${digits}`;

  return digits.slice(0, RU_PHONE_DIGITS + 4);
}

/** Похож ли набор цифр на российский номер: семёрка и ещё десять. */
function looksRussian(digits: string): boolean {
  return digits.startsWith("7") && digits.length <= RU_PHONE_DIGITS;
}

/** Читаемый вид прямо во время набора: «+7 900 123-45-67». */
export function formatRuPhone(raw: string): string {
  const digits = ruPhoneDigits(raw);
  if (!digits) return "";

  // Не наш формат — показываем как есть, чтобы ошибка была видна гостю.
  if (!looksRussian(digits)) return `+${digits}`;

  const rest = digits.slice(1);
  const parts = [rest.slice(0, 3), rest.slice(3, 6), rest.slice(6, 8), rest.slice(8, 10)].filter(Boolean);

  if (parts.length === 0) return "+7";

  const [code, first, second, third] = parts;
  return `+7 ${code}${first ? ` ${first}` : ""}${second ? `-${second}` : ""}${third ? `-${third}` : ""}`;
}

export function isRuPhoneComplete(raw: string): boolean {
  const digits = ruPhoneDigits(raw);
  return looksRussian(digits) && digits.length === RU_PHONE_DIGITS;
}
